/**
 * Client collection model (v49) — the mirror of the server's `collectionState`
 * pushes: the garage (vehicles you have claimed) and the wardrobe (appearances you
 * have collected). SocialModel pattern: adopt the whole push, bump `rev` so panels
 * re-render off one counter.
 *
 * The server sends the UNION of the implicit starter grants and the persisted
 * ledger, so nothing here needs to know which grants are implicit. This model is a
 * MIRROR, never an authority: every selection round-trips as a `collection` intent
 * and the echo is what makes it true.
 */
import { VEHICLES, type CollectionMsg, type CollectionStateMsg } from "@hyperlight/shared";

export class GarageModel {
  /** Bumped on every adopted push — panels compare and re-render. */
  rev = 0;
  /** Vehicle ids this character owns. */
  vehicles: string[] = [];
  /** Wardrobe tokens, "<part>:<variant>" (P3 reads these; P2 only carries them). */
  gear: string[] = [];
  /** The chassis that deploys, or null. */
  active: string | null = null;
  /** True once a push has landed — panels show "…" rather than "empty" before it. */
  seeded = false;
  private seq = 0;
  private selectedByType: Partial<Record<"ground" | "flying", string>> = {};

  /** The garage's last choice in each category, reconciled against ownership. */
  rememberSelection(id: string | null): void {
    if (id && VEHICLES[id] && this.owns(id)) this.selectedByType[VEHICLES[id].flight ? "flying" : "ground"] = id;
  }

  selectedFor(type: "ground" | "flying"): string | null {
    this.rememberSelection(this.active);
    const matches = (id: string) => this.owns(id) && !!VEHICLES[id] && (VEHICLES[id].flight ? "flying" : "ground") === type;
    const selected = this.selectedByType[type];
    return selected && matches(selected) ? selected : this.vehicles.find(matches) ?? null;
  }

  constructor(private handlers: { toast: (line: string) => void }) {}

  /** Stamp the next op seq (monotonic per session — the social idiom). */
  op(m: Omit<CollectionMsg, "t" | "seq">): CollectionMsg {
    return { t: "collection", seq: ++this.seq, ...m };
  }

  owns(vehicleId: string): boolean {
    return this.vehicles.includes(vehicleId);
  }

  /** Adopt an authoritative push. A refusal toasts and still adopts the state —
   *  the server's view of the garage is the truth either way. */
  apply(msg: CollectionStateMsg): void {
    const hadSeeded = this.seeded;
    const before = this.active;
    this.vehicles = [...msg.vehicles];
    this.gear = [...msg.gear];
    this.active = msg.vehicle;
    this.rememberSelection(this.active);
    this.seeded = true;
    this.rev++;
    if (msg.err) this.handlers.toast(msg.err);
    // Only announce a change the player did not just watch happen at login.
    else if (hadSeeded && before !== this.active && this.active) {
      this.handlers.toast(`ACTIVE VEHICLE — ${this.active.toUpperCase().replace(/-/g, " ")}`);
    }
  }
}
